import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

// Global flag to prevent multiple gift bag additions across all instances
let isAddingGiftBagGlobally = false;

/**
 * A custom element that manages the gift bag option checkbox.
 * When a product is added to cart with this option checked,
 * it automatically adds the gift bag product to the cart as well.
 *
 * @typedef {object} GiftBagOptionRefs
 * @property {HTMLInputElement} giftBagCheckbox - The checkbox input element.
 * @property {HTMLElement | undefined} imagePreview - The image preview element.
 *
 * @extends Component<GiftBagOptionRefs>
 */
class GiftBagOptionComponent extends Component {
  requiredRefs = ['giftBagCheckbox'];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    console.log('Gift bag component connected', this);

    const { signal } = this.#abortController;
    
    // Listen for form submissions - this is the most reliable way to detect when
    // a product is being added (not updated/removed)
    // Find the product form on the page
    const productForm = document.querySelector('product-form-component') || 
                       document.querySelector('form[action*="/cart/add"]');
    
    console.log('Gift bag: found product form?', productForm);
    
    if (productForm) {
      // Use capture phase to ensure we run AFTER the form has been submitted
      productForm.addEventListener('submit', (event) => {
        console.log('Gift bag: form submit detected', {
          checkboxChecked: this.refs.giftBagCheckbox?.checked,
          defaultPrevented: event.defaultPrevented,
          globalFlag: isAddingGiftBagGlobally
        });
        
        // IMPORTANT: Don't prevent default - let the form submit normally
        // Use a longer delay to ensure the main product is fully added first
        setTimeout(() => {
          console.log('Gift bag: timeout callback', {
            checkboxChecked: this.refs.giftBagCheckbox?.checked,
            globalFlag: isAddingGiftBagGlobally
          });
          
          // Only add gift bag if checkbox is checked and we're not already adding one
          if (this.refs.giftBagCheckbox.checked && !isAddingGiftBagGlobally) {
            const giftBagVariantId = this.refs.giftBagCheckbox.value;
            if (giftBagVariantId) {
              const quantityInput = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="quantity"]'));
              const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;
              console.log('Gift bag: will add', quantity, 'gift bags');
              this.#addGiftBagToCart(giftBagVariantId, quantity);
            }
          } else if (isAddingGiftBagGlobally) {
            console.log('Gift bag: already in process, skipping');
          } else {
            console.log('Gift bag: checkbox not checked, skipping');
          }
        }, 800); // Even longer delay to ensure main product goes through first
      }, { signal, capture: false, passive: true }); // passive: true ensures we don't block
    }
    
    // NOTE: localStorage functionality removed - checkbox always starts unchecked
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Adds the gift bag product to the cart
   * @param {string} variantId - The variant ID of the gift bag
   * @param {number} quantity - The quantity to add
   */
  async #addGiftBagToCart(variantId, quantity) {
    // Prevent multiple simultaneous additions using global flag
    if (isAddingGiftBagGlobally) {
      console.log('Gift bag: already adding, preventing duplicate');
      return;
    }
    
    isAddingGiftBagGlobally = true;
    console.log('Gift bag: starting to add gift bag');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', String(quantity));

    // Get section IDs to update
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    formData.append('sections', cartItemComponentsSectionIds.join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      const data = await response.json();

      if (!data.status) {
        console.log('Gift bag: successfully added');
        // Success - dispatch cart add event for gift bag
        document.dispatchEvent(
          new CartAddEvent({}, variantId, {
            source: 'gift-bag-option',
            itemCount: quantity,
            productId: this.dataset.productId,
            sections: data.sections,
          })
        );
      } else {
        // Error - log it but don't interrupt the main product add
        console.warn('Gift bag could not be added:', data.message);
      }
    } catch (error) {
      console.error('Error adding gift bag to cart:', error);
    } finally {
      // Reset the flag after a short delay to allow events to process
      setTimeout(() => {
        isAddingGiftBagGlobally = false;
        console.log('Gift bag: reset adding flag');
      }, 500);
    }
  }

}

if (!customElements.get('gift-bag-option-component')) {
  customElements.define('gift-bag-option-component', GiftBagOptionComponent);
}
